import React, { useState, useCallback } from 'react';
import { isEmpty, pathOr } from 'ramda';
import {
  Card,
  Stack,
  DisplayText,
  Page,
  ProgressBar,
  FormLayout,
  TextField,
  Select,
  Button,
  TextStyle,
  ResourceList,
  ResourceItem,
  Modal,
  EmptyState,
} from '@shopify/polaris';
import { Redirect } from '@shopify/app-bridge/actions';
import { useMutation } from '@apollo/react-hooks';
import {
  CREATE_DEPARTMENT,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
  UPDATE_MEMBER,
  ADD_MEMBER,
  REMOVE_MEMBER,
  UPDATE_LOCATION,
  ADD_LOCATION,
  REMOVE_SHOP_LOCATION,
  CREATE_SHOP_SUBSCRIPTION,
  CANCEL_SHOP_SUBSCRIPTION,
} from '../apollo/mutation';
import { INDUSTRY_OPTIONS, EMPLOYEE_SIZE } from '../../utils/formDefaultValues';
import Footer from './Footer';
import { shophireLogo } from '../../utils/shophireLogo';
import { validateEmail } from '../../utils/normalizer';

const stepAnnotations = [
  'Let’s get your company set up for hiring success, it’s 5 steps and will take 3 minutes.',
  'Organize your jobs by the different departments within your organization. Job seekers can search & filter job openings on your career site.',
  'Get your team involved in the hiring process with notifications about jobs and candidates.',
  'Speed up the the job posting process by adding hiring locations to assign job openings.',
];
const stepTitles = ['Company Profile', 'Departments', 'Hiring Team', 'Hiring Locations', 'Account Plan & Billing'];
const stepUrls = [
  'https://help.shophire.co/article/23-how-to-update-my-company-name',
  'https://help.shophire.co/article/46-departments',
  'https://help.shophire.co/article/27-hiring-team',
  'https://help.shophire.co/article/9-hiring-locations',
  'https://help.shophire.co/article/8-settings',
];

export default ({
  data = {},
  shopifyToken,
  refetch = () => {},
  onUpdateProfile = () => {},
  onDismiss = () => {},
  apiKey,
  shopOrigin,
  app,
}) => {
  const [step, setStep] = useState(0);
  const [departmentModal, setDepartmentModal] = useState(false);
  const [removeDepartmentModal, setRemoveDepartmentModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [removeMemberModal, setRemoveMemberModal] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [removeLocationModal, setRemoveLocationModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { shop = {} } = { ...data };
  const { careerPortal = {}, profile = {}, teamMembers = [], locations = [], currentSubscription = {} } = { ...shop };
  const { departments = [] } = { ...careerPortal };
  const [form, setForm] = useState({ ...profile });
  const [createDepartment] = useMutation(CREATE_DEPARTMENT);
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT);
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT);
  const [updateMember] = useMutation(UPDATE_MEMBER);
  const [deleteMember] = useMutation(REMOVE_MEMBER);
  const [addMember] = useMutation(ADD_MEMBER);
  const [updateLocation] = useMutation(UPDATE_LOCATION);
  const [addLocation] = useMutation(ADD_LOCATION);
  const [deleteLocation] = useMutation(REMOVE_SHOP_LOCATION);

  const redirect = Redirect.create(app);
  const [cancelShopSubscription] = useMutation(CANCEL_SHOP_SUBSCRIPTION);
  const [createShopSubscription] = useMutation(CREATE_SHOP_SUBSCRIPTION, {
    onCompleted(data) {
      onDismiss();
      redirect.dispatch(Redirect.Action.REMOTE, data.createShopSubscription.confirmationUrl);
    },
  });

  const createSubscription = (subscriptionType) => () => {
    const response = createShopSubscription({
      variables: {
        shopifyToken,
        subscriptionType,
      },
    });

    const errors = pathOr([], ['data', 'createShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const cancelSubscription = () => {
    const response = cancelShopSubscription({
      variables: {
        shopifyToken,
        subscriptionId: currentSubscription.id,
      },
    });

    setTimeout(() => {
      onDismiss();
    }, 500);

    const errors = pathOr([], ['data', 'cancelShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const isPremiumPlan = currentSubscription?.subscriptionType === 'Premium';
  const isGrowthPlan = currentSubscription?.subscriptionType === 'Growth';
  const isBusinessPlan = currentSubscription?.subscriptionType === 'Business';
  const isStarterPlan = isEmpty(currentSubscription || {});

  const handleDepartmentModal = useCallback(() => {
    setDepartmentModal(!departmentModal);
  }, [departmentModal]);

  const handleMemberModal = useCallback(() => {
    setMemberModal(!memberModal);
  }, [memberModal]);

  const handleLocationModal = useCallback(() => {
    setLocationModal(!locationModal);
  }, [locationModal]);

  const handleRemoveDepartmentModal = useCallback(() => setRemoveDepartmentModal(!removeDepartmentModal), [
    removeDepartmentModal,
  ]);

  const handleRemoveMemberModal = useCallback(() => setRemoveMemberModal(!removeMemberModal), [removeMemberModal]);

  const handleRemoveLocationModal = useCallback(() => setRemoveLocationModal(!removeLocationModal), [removeLocationModal]);

  const openRemoveDepartmentModal = (department) => {
    setSelectedDepartment(department);
    setRemoveDepartmentModal(true);
  };

  const openRemoveMemberModal = (member) => {
    setSelectedMember(member);
    setRemoveMemberModal(true);
  };

  const openRemoveLocationModal = (location) => {
    setSelectedLocation(location);
    setRemoveLocationModal(true);
  };

  const handleUpdateProfile = () => {
    onUpdateProfile({
      variables: {
        companyName: form.companyName,
        phoneNumber: form.phoneNumber,
        contactEmail: form.contactEmail,
        industry: form.industry,
        employeeSize: form.employeeSize,
        careerPageLink: form.careerPageLink,
        shopifyToken,
        branding: true,
        recaptchaEnabled: true,
        weeklyApplicantSummary: false,
      },
    })
      .then((response) => {
        if (response.data.updateProfile.errors.length === 0) {
          setStep(step + 1);
        }
      })
      .catch((e) => {
        console.log(e.graphQLErrors[0].message);
      });
  };

  const handleEditDepartmentModal = (department) => {
    if (!!department) {
      let { id: departmentId, headingText: departmentName } = { ...department };
      setForm({
        ...form,
        departmentId,
        departmentName,
      });
      setDepartmentModal(true);
      setSelectedDepartment(department);
    } else {
      setForm({
        ...form,
        departmentId: '',
        departmentName: '',
        departmentId: '',
      });
      setDepartmentModal(true);
      setSelectedDepartment(null);
    }
  };

  const handleEditMemberModal = (member) => {
    if (!!member) {
      let { id: memberId, firstName: memberFirstName, lastName: memberLastName, email: memberEmail } = { ...member };
      setForm({
        ...form,
        memberId,
        memberFirstName,
        memberLastName,
        memberEmail,
      });
      setMemberModal(true);
      setSelectedMember(department);
    } else {
      setForm({
        ...form,
        memberFirstName: '',
        memberLastName: '',
        memberEmail: '',
        memberId: '',
      });
      setMemberModal(true);
      setSelectedMember(null);
    }
  };

  const handleEditLocationModal = (location) => {
    if (!!location) {
      let { address1, address2, city, state: statee, country, zipCode, id: locationId, name: locationName } = { ...location };
      setForm({
        ...form,
        address1,
        address2,
        city,
        statee,
        country,
        zipCode,
        locationId,
        locationName,
      });
      setLocationModal(true);
      setSelectedLocation(location);
    } else {
      setForm({
        ...form,
        address1: '',
        address2: '',
        city: '',
        statee: '',
        country: '',
        zipCode: '',
        locationId: '',
        locationName: '',
      });
      setLocationModal(true);
      setSelectedLocation(null);
    }
  };

  const validate = () => {
    switch (step) {
      case 0:
        return (
          !!form.companyName && !!form.contactEmail && !!form.employeeSize && !!form.industry && validateEmail(form.contactEmail)
        );
      case 1:
        return (departments || []).length > 0;
      case 2:
        return (teamMembers || []).length > 0;
      case 3:
        return (locations || []).length > 0;
      case 4:
        return true;
      default:
        false;
    }
  };

  const onNext = () => {
    switch (step) {
      case 0:
        handleUpdateProfile();
        break;
      case 4:
        onDismiss();
        break;
      default:
        setStep(step + 1);
        break;
    }
  };

  const updateField = (name) => (value) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  const markupProfile = (
    <Card.Section>
      <FormLayout>
        <TextField
          id='companyName'
          label='Company name'
          value={form.companyName}
          name='companyName'
          onChange={updateField('companyName')}
        />
        <TextField
          id='contactEmail'
          label='Email address'
          value={form.contactEmail}
          name='contactEmail'
          type='email'
          onChange={updateField('contactEmail')}
        />
        <FormLayout.Group>
          <Select label='Industry' options={INDUSTRY_OPTIONS} onChange={updateField('industry')} value={form.industry} />
          <Select
            label='Number of employees'
            options={EMPLOYEE_SIZE}
            onChange={updateField('employeeSize')}
            value={form.employeeSize}
          />
        </FormLayout.Group>
      </FormLayout>
    </Card.Section>
  );

  const markupDepartments = (
    <Card.Section>
      {isEmpty(departments) ? (
        <EmptyState heading='Departments' action={{ content: 'Add department', onAction: () => handleEditDepartmentModal(null) }}>
          <p>Categorize your jobs by department.</p>
        </EmptyState>
      ) : (
        <div className='DispositionCodes__list'>
          <ResourceList
            items={departments}
            renderItem={(item) => {
              const { headingText, id } = item;
              return (
                <ResourceItem
                  id={id}
                  shortcutActions={[
                    {
                      content: 'Edit',
                      onAction: () => handleEditDepartmentModal(item),
                    },
                    {
                      content: 'Remove',
                      onAction: () => openRemoveDepartmentModal(item),
                    },
                  ]}>
                  <p>{headingText}</p>
                </ResourceItem>
              );
            }}
          />
        </div>
      )}
    </Card.Section>
  );

  const markupMembers = (
    <Card.Section>
      {isEmpty(teamMembers) ? (
        <EmptyState heading='Hiring team' action={{ content: 'Add team member', onAction: () => handleEditMemberModal(null) }}>
          <p>Add team members to your account, so they can receive notifications about jobs and candidates.</p>
        </EmptyState>
      ) : (
        <div className='DispositionCodes__list'>
          <ResourceList
            items={teamMembers}
            renderItem={(item) => {
              const { firstName, lastName, email, id } = item;
              return (
                <ResourceItem
                  id={id}
                  shortcutActions={[
                    {
                      content: 'Edit',
                      onAction: () => handleEditMemberModal(item),
                    },
                    {
                      content: 'Remove',
                      onAction: () => openRemoveMemberModal(item),
                    },
                  ]}>
                  <TextStyle variation='strong'>
                    {firstName} {lastName}
                  </TextStyle>
                  <div>{email}</div>
                </ResourceItem>
              );
            }}
          />
        </div>
      )}
    </Card.Section>
  );

  const markupLocations = (
    <Card.Section>
      {isEmpty(locations) ? (
        <EmptyState
          heading='Hiring locations'
          action={{ content: 'Add location', onAction: () => handleEditLocationModal(null) }}>
          <p>Manage the locations where your company will be hiring new employees.</p>
        </EmptyState>
      ) : (
        <ResourceList
          items={locations}
          renderItem={(item) => {
            const { address1, address2, city, id, name, state } = item;
            return (
              <ResourceItem
                id={id}
                shortcutActions={[
                  {
                    content: 'Edit',
                    onAction: () => handleEditLocationModal(item),
                  },
                  {
                    content: 'Remove',
                    onAction: () => openRemoveLocationModal(item),
                  },
                ]}>
                <h3>
                  <TextStyle variation='strong'>{name}</TextStyle>
                </h3>
                <div>{address1}</div>
                <div>{address2}</div>
                <div>
                  {city}, {state}
                </div>
              </ResourceItem>
            );
          }}
        />
      )}
    </Card.Section>
  );

  const markupAccountPlan = (
    <Card.Section>
      <div className='plans-cards-container'>
        <div className={`plan-card ${isStarterPlan ? 'is-active' : ''}`}>
          <h1 className='plan-card-title'>Starter</h1>
          <h2 className='plan-card-subtitle'>Free</h2>
          <ul className='plan-card-list'>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> 3 Jobs
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Google for Jobs
            </li>
          </ul>
          {!isStarterPlan && (
            <button
              type='button'
              className='plan-card-button'
              onClick={() => {
                cancelSubscription();
                refetch();
                toggleIsUpgradeModalOpen();
              }}>
              {isStarterPlan ? 'Get started' : 'Downgrade plan'}
            </button>
          )}
        </div>
        <div className={`plan-card ${isGrowthPlan ? 'is-active' : ''}`}>
          <h1 className='plan-card-title'>Growth</h1>
          <h2 className='plan-card-subtitle'>
            $29<span style={{ fontSize: '10px' }}>month</span>
          </h2>
          <ul className='plan-card-list'>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> 5 Jobs
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Webhooks
            </li>
          </ul>
          {!isGrowthPlan && (
            <button
              onClick={() => {
                createSubscription('Growth')();
              }}
              type='button'
              className='plan-card-button'>
              {isStarterPlan ? 'Upgrade plan' : 'Downgrade plan'}
            </button>
          )}
        </div>
        <div className={`plan-card ${isBusinessPlan ? 'is-active' : ''}`}>
          <h1 className='plan-card-title'>Business</h1>
          <h2 className='plan-card-subtitle'>
            $39<span style={{ fontSize: '10px' }}>month</span>
          </h2>
          <ul className='plan-card-list'>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> 10 Jobs
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Webhooks
            </li>
          </ul>
          {!isBusinessPlan && (
            <button
              onClick={() => {
                createSubscription('Business')();
              }}
              type='button'
              className='plan-card-button'>
              {isStarterPlan || isGrowthPlan ? 'Upgrade plan' : 'Downgrade plan'}
            </button>
          )}
        </div>
        <div className={`plan-card ${isPremiumPlan ? 'is-active' : ''}`}>
          <h1 className='plan-card-title'>Premium</h1>
          <h2 className='plan-card-subtitle'>
            $49<span style={{ fontSize: '10px' }}>month</span>
          </h2>
          <ul className='plan-card-list'>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Unlimited Jobs
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
            </li>
            <li style={{ marginBottom: '10px' }}>
              <span className='plan-card-check-item'>&#10003;</span> Webhooks
            </li>
          </ul>
          {!isPremiumPlan && (
            <button
              onClick={() => {
                createSubscription('Premium')();
              }}
              type='button'
              className='plan-card-button'>
              {isStarterPlan || isGrowthPlan || isBusinessPlan ? 'Upgrade plan' : 'Downgrade plan'}
            </button>
          )}
        </div>
      </div>
    </Card.Section>
  );

  return (
    <Page>
      <Stack vertical>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <img src={shophireLogo} />
        </div>
        <DisplayText size={'medium'}>
          <p style={{ textAlign: 'center' }}>Welcome. Tell us a little but about your company.</p>
        </DisplayText>
        <br />
        <Card>
          <Card.Section>
            <Stack alignment='center'>
              <Stack.Item fill>
                <DisplayText size='small'>
                  <b>{stepTitles[step]}</b>
                </DisplayText>
              </Stack.Item>
              <Stack.Item>
                <TextStyle variation='subdued'>
                  ({step + 1}/{stepTitles.length})
                </TextStyle>
              </Stack.Item>
            </Stack>
            {!!stepAnnotations[step] && (
              <div style={{ marginTop: '10px' }}>
                <TextStyle variation={'subdued'}>{stepAnnotations[step]}</TextStyle>
              </div>
            )}
          </Card.Section>
          {step === 0 && markupProfile}
          {step === 1 && markupDepartments}
          {step === 2 && markupMembers}
          {step === 3 && markupLocations}
          {step === 4 && markupAccountPlan}
          <Card.Section>
            <ProgressBar size={'small'} progress={(step + 1) * (100 / stepTitles.length)} />
          </Card.Section>
        </Card>
        <Stack>
          {step === 1 && <Button onClick={() => handleEditDepartmentModal(null)}>Add department</Button>}
          {step === 2 && <Button onClick={() => handleEditMemberModal(null)}>Add team member</Button>}
          {step === 3 && <Button onClick={() => handleEditLocationModal(null)}>Add location</Button>}
          <Stack.Item fill></Stack.Item>
          <Button
            disabled={step === 0}
            onClick={() => {
              setStep(step - 1);
            }}>
            {'Back'}
          </Button>
          <Button primary disabled={!validate()} onClick={onNext}>
            {step === stepTitles.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Stack>
      </Stack>
      <Footer link={stepUrls[step]} name={stepTitles[step]} />
      <Modal
        open={removeDepartmentModal}
        onClose={handleRemoveDepartmentModal}
        title='Remove department'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            deleteDepartment({
              variables: {
                shopifyToken,
                departmentId: selectedDepartment.id,
              },
            })
              .then(() => {
                handleRemoveDepartmentModal();
                setSelectedDepartment(null);
                refetch();
              })
              .catch(() => {});
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleRemoveDepartmentModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this department?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={departmentModal}
        onClose={handleDepartmentModal}
        title={selectedDepartment?.id ? 'Update department' : 'Add department'}
        primaryAction={{
          content: selectedDepartment?.id ? 'Update department' : 'Add department',
          onAction: () => {
            if (!selectedDepartment?.id) {
              createDepartment({
                variables: {
                  headingText: form.departmentName,
                  shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            } else {
              updateDepartment({
                variables: {
                  departmentId: selectedDepartment.id,
                  headingText: form.departmentName,
                  subheadingText: selectedDepartment.subheadingText,
                  isHighlight: selectedDepartment.isHighlight || false,
                  shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            }
            handleDepartmentModal();
            setSelectedDepartment(null);
          },
          disabled: !form.departmentName,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                departmentName: '',
              });
              setSelectedDepartment(null);
              handleDepartmentModal();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <TextField
              id='departmentName'
              label='Department name'
              value={form.departmentName}
              name='departmentName'
              onChange={updateField('departmentName')}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={removeMemberModal}
        onClose={handleRemoveMemberModal}
        title='Remove team member'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            deleteMember({
              variables: {
                shopifyToken,
                memberId: selectedMember.id,
              },
            })
              .then(() => {
                handleRemoveMemberModal();
                setSelectedMember(null);
                refetch();
              })
              .catch(() => {});
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleRemoveMemberModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this team member?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={memberModal}
        onClose={handleMemberModal}
        title={selectedMember?.id ? 'Update team member' : 'Add team member'}
        primaryAction={{
          content: selectedMember?.id ? 'Update team member' : 'Add team member',
          onAction: () => {
            if (!selectedMember?.id) {
              addMember({
                variables: {
                  firstName: form.memberFirstName,
                  lastName: form.memberLastName,
                  email: form.memberEmail,
                  shopifyToken: shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            } else {
              updateMember({
                variables: {
                  firstName: form.memberFirstName,
                  lastName: form.memberLastName,
                  email: form.memberEmail,
                  memberId: form.memberId,
                  shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            }
            handleMemberModal();
            setSelectedMember(null);
          },
          disabled: !form.memberFirstName || !form.memberLastName || !form.memberEmail || !validateEmail(form.memberEmail),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                memberFirstName: '',
                memberLastName: '',
                memberEmail: '',
              });
              setSelectedMember(null);
              handleMemberModal();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <TextField
              id='memberFirstName'
              label='First name'
              value={form.memberFirstName}
              name='memberFirstName'
              onChange={updateField('memberFirstName')}
            />
            <TextField
              id='memberLastName'
              label='Last name'
              value={form.memberLastName}
              name='memberLastName'
              onChange={updateField('memberLastName')}
            />
            <TextField
              id='memberEmail'
              label='Email'
              value={form.memberEmail}
              name='memberEmail'
              onChange={updateField('memberEmail')}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={removeLocationModal}
        onClose={handleRemoveLocationModal}
        title='Remove Location'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            deleteLocation({
              variables: {
                shopifyToken,
                locationId: selectedLocation.id,
              },
            })
              .then(() => {
                handleRemoveLocationModal();
                setSelectedLocation(null);
                refetch();
              })
              .catch(() => {});
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleRemoveLocationModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this hiring location?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={locationModal}
        onClose={handleLocationModal}
        title={selectedLocation?.id ? 'Update location' : 'Add location'}
        primaryAction={{
          content: selectedLocation?.id ? 'Update location' : 'Add location',
          onAction: () => {
            if (!selectedLocation?.id) {
              addLocation({
                variables: {
                  address1: form.address1,
                  address2: form.address2,
                  city: form.city,
                  country: form.country,
                  state: form.statee,
                  zipCode: form.zipCode,
                  name: form.locationName,
                  shopifyToken: shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            } else {
              updateLocation({
                variables: {
                  name: form.locationName,
                  address1: form.address1,
                  address2: form.address2,
                  city: form.city,
                  state: form.statee,
                  country: form.country,
                  zipCode: form.zipCode,
                  locationId: form.locationId,
                  shopifyToken,
                },
              })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            }
            handleLocationModal();
            setSelectedLocation(null);
          },
          disabled: !form.address1 || !form.locationName || !form.statee || !form.country || !form.zipCode || !form.city,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                address1: '',
                address2: '',
                city: '',
                statee: '',
                country: '',
                zipCode: '',
                locationId: '',
                locationName: '',
              });
              setSelectedLocation(null);
              handleLocationModal();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <div>
              <TextField
                id='locationName'
                label='Name'
                value={form.locationName}
                name='locationName'
                onChange={updateField('locationName')}
              />
            </div>
            <TextField id='address1' label='Address' value={form.address1} name='address1' onChange={updateField('address1')} />
            <TextField
              id='address2'
              label='Apartment, suite, etc (Optional)'
              value={form.address2}
              name='address2'
              onChange={updateField('address2')}
            />
            <FormLayout.Group>
              <div>
                <TextField id='city' label='City' value={form.city} name='city' onChange={updateField('city')} />
              </div>
              <div>
                <TextField id='zipCode' label='Zip code' value={form.zipCode} name='zipCode' onChange={updateField('zipCode')} />
              </div>
            </FormLayout.Group>
            <FormLayout.Group>
              <div>
                <TextField id='country' label='Country' value={form.country} name='zipCode' onChange={updateField('country')} />
              </div>
              <div>
                <TextField id='state' label='State' value={form.statee} name='state' onChange={updateField('statee')} />
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
};
